import React from "react";
import { KSelect, KTextInput } from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/styles";
import { Theme, Typography } from "@material-ui/core";

import { useGetTemplateParameters } from "../../../requests/GetTemplateParameters";
import { ChangableVehicleTemplate } from "../../../facades/VehicleTemplate";
import { OdometerSource } from "../../../typings/RawVehicleTemplate";

import VehicleTemplateList from "../../VehicleTemplateList";
import ToggleButton from "../../ToggleButton";
import { useBillingTariffs } from "../../../../devices/hooks/useBillingTariffs";

const useStyles = makeStyles(({ palette }: Theme) => ({
  contianer: {
    display: "flex",
    gap: 16,
    rowGap: 6,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  defaultToggle: {},
  spacer: {
    display: "flex",
    flex: 1,
  },
  defaultToggleLabel: {
    marginBottom: 4,
    color: palette.common.black,
  },
}));

const styles = {
  topStyles: { marginTop: 0 },
};

const isDefaultTemplateStates = [
  { label: "Regular", value: "false" },
  { label: "Default", value: "true" },
];

const odometerSourceOptions = [
  { label: "CAN", value: String(OdometerSource.Can) },
  { label: "Tachograph", value: String(OdometerSource.Tco) },
  { label: "GPS", value: String(OdometerSource.Gps) },
];

type TableEditorProps = Omit<ChangableVehicleTemplate, "companies"> & {
  onTemplateChanged: (template: Partial<ChangableVehicleTemplate>) => void;
};

function clamp(number: number, min: number, max: number) {
  return Math.max(min, Math.min(number, max));
}

const TableEditor: React.FC<TableEditorProps> = ({
  onTemplateChanged = () => {},
  ...template
}) => {
  const classes = useStyles();
  const { tariffs } = useBillingTariffs();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTemplateChanged({ [e.target.name]: e.target.value });
  };

  const handleIgnitionSelect = (el) => {
    onTemplateChanged({ ignitionValue: el?.label || "" });
  };

  const handleInternalTariffSelect = (el) => {
    onTemplateChanged({ internalTariffId: el?.value || "" });
  };

  const handleOdometerSourceSelect = (el) => {
    onTemplateChanged({
      odometerSource: el ? Number(el.value) : OdometerSource.Can,
    });
  };

  const { data: availableParameters = [] } = useGetTemplateParameters();

  return (
    <>
      <div className={classes.contianer}>
        <KTextInput
          label="Name"
          value={template.name}
          name="name"
          onChange={handleChange}
          required
          placeholder="DXP Standard Template"
          style={styles.topStyles}
          width="250px"
        />

        <KSelect
          elements={template.columns.map((column) => ({
            label: column.technicalName,
            value: column.technicalName,
          }))}
          height={clamp(template.columns.length, 1, 6) * 35}
          value={template.ignitionValue}
          label="Ignition Value"
          style={styles.topStyles}
          onSelect={handleIgnitionSelect}
          isClearable={true}
        />

        <KTextInput
          width="300px"
          name="description"
          label="Description"
          onChange={handleChange}
          style={styles.topStyles}
          value={template.description}
          placeholder="Only use this for DXP"
        />

        <div>
          <Typography variant="body2" className={classes.defaultToggleLabel}>
            Is Default Template?
          </Typography>

          <ToggleButton
            className={classes.defaultToggle}
            states={isDefaultTemplateStates}
            currectState={template.isDefaultTemplate.toString()}
            onChangeState={(state) =>
              onTemplateChanged({ isDefaultTemplate: state === "true" })
            }
          />
        </div>

        <KSelect
          elements={odometerSourceOptions}
          value={
            odometerSourceOptions.find(
              (option) => option.value === String(template.odometerSource),
            )?.label
          }
          label="Odometer Source"
          style={styles.topStyles}
          onSelect={handleOdometerSourceSelect}
        />

        <KTextInput
          label="Error codes mapping name"
          value={template.errorCodesMappingName || ""}
          name="errorCodesMappingName"
          onChange={handleChange}
          placeholder="Add error codes mapping name from tab Value Mappings"
          style={styles.topStyles}
          width="250px"
        />
        <KSelect
          elements={tariffs.map((tariff) => ({
            label: tariff?.companyData?.name || "n.a.",
            value: tariff._id,
          }))}
          value={
            tariffs.find((tariff) => tariff._id === template.internalTariffId)
              ?.companyData?.name || "n.a."
          }
          label="Billing Tariff *"
          style={styles.topStyles}
          onSelect={handleInternalTariffSelect}
        />
        <KTextInput
          label="Billing ID"
          value={
            tariffs.find((tariff) => tariff._id === template.internalTariffId)
              ?.license
          }
          name="billingTariffId"
          onChange={() => {}}
          style={styles.topStyles}
          width="250px"
          disabled
        />
      </div>

      <VehicleTemplateList
        name={template.name}
        columns={template.columns}
        description={template.description}
        onTemplateChanged={onTemplateChanged}
        ignitionValue={template.ignitionValue}
        availableParameters={availableParameters}
        isDefaultTemplate={template.isDefaultTemplate}
        displayColumns={template.displayColumns}
        errorCodesMappingName={template.errorCodesMappingName}
        internalTariffId={template.internalTariffId}
        odometerSource={template.odometerSource}
        tags={template.tags}
        owner={template.owner}
      />
    </>
  );
};

export default TableEditor;
